// ===================================================================
// CENTRALIZED ANIMATION CONFIGURATION
// All hero animation parameters in one place for easy fine-tuning
// ===================================================================

export const ANIMATION_CONFIG = {
  // ----------------------------------------------------------------
  // TIMING CONFIGURATION
  // ----------------------------------------------------------------
  timing: {
    // Row generation delays (milliseconds) - when new rows appear
    rowAddDelay: {
      mobile_small: 4725,  // < 480px: 4.725 seconds
      mobile: 4200,        // < 768px: 4.2 seconds  
      tablet: 3675,        // < 1024px: 3.675 seconds
      desktop: 3150        // >= 1024px: 3.15 seconds
    },
    
    // Initial delay before first row appears (ms)
    initialDelay: 1500,
    
    // Row expiration time (ms) - when rows are removed
    rowExpiration: 30000, // 30 seconds
    
    // Aggressive cleanup when memory is high (ms)
    aggressiveExpiration: 15000, // 15 seconds
    
    // Animation frame rate (fps)
    targetFPS: 60,
    
    // Fade-in animation duration factor
    fadeInSpeed: 2, // Lower = faster fade
    
    // Resize debounce delay (ms) - shorter for quicker reset
    resizeDebounce: 150,
    
    // Image preload cache size
    preloadCacheSize: 50
  },
  
  // ----------------------------------------------------------------
  // SPACING CONFIGURATION
  // ----------------------------------------------------------------
  spacing: {
    // Row spacing based on viewport width (pixels)
    // These match the card gaps for visual consistency
    rowGap: {
      mobile_small: 6,     // < 640px
      mobile: 8,           // < 1024px
      tablet: 10,          // < 1440px
      desktop: 12,         // < 1920px
      desktop_large: 14    // >= 1920px
    },
    
    // Row height (pixels) - responsive
    rowHeight: {
      mobile_small: 90,    // < 480px
      mobile: 100,         // < 640px
      tablet_small: 110,   // < 768px
      tablet: 120,         // < 1024px
      desktop_small: 130,  // < 1440px
      desktop: 140         // >= 1440px
    },
    
    // Card dimensions
    cardWidth: {
      mobile: 70,
      desktop: 90
    },
    
    // Container padding
    padding: {
      mobile: 16,
      desktop: 32
    },
    
    // Card gap
    cardGap: {
      mobile: 4,
      desktop: 8
    },
    
    // Buffer rows above/below viewport
    bufferRows: {
      mobile: 2,
      desktop: 3
    },
    
    // Maximum total rows to keep in memory
    maxRows: {
      minimum: 20,
      buffer: 5 // Added to viewport.rows
    }
  },
  
  // ----------------------------------------------------------------
  // CARDS PER ROW CONFIGURATION
  // ----------------------------------------------------------------
  cardsPerRow: {
    mobile_small: 6,      // < 480px
    mobile: 8,            // < 640px
    tablet_small: 10,     // < 768px
    tablet: 12,           // < 1024px
    desktop_small: 16,    // < 1440px
    desktop: 20,          // < 1920px
    desktop_large: 24     // >= 1920px
  },
  
  // ----------------------------------------------------------------
  // MOTION CONFIGURATION
  // ----------------------------------------------------------------
  motion: {
    // Scroll speeds (pixels per frame)
    scrollSpeed: {
      mobile_small: 0.3,   // < 480px
      mobile: 0.5,         // < 768px
      tablet: 0.6,         // < 1024px
      desktop_small: 0.7,  // < 1440px
      desktop: 0.8         // >= 1440px
    },
    
    // Speed multipliers for row acceleration
    acceleration: {
      // Initial appearance (0-0.5 seconds)
      initial: {
        mobile: 0.2,
        desktop: 0.3
      },
      // Acceleration phase (0.5-2 seconds)
      accelerating: {
        mobile_min: 0.2,
        mobile_max: 0.5,
        tablet_max: 0.6,
        desktop_max: 0.8
      },
      // Normal speed (after 2 seconds)
      normal: {
        mobile: 0.5,
        tablet: 0.6,
        desktop: 0.8
      }
    },
    
    // Time periods for acceleration phases (seconds)
    accelerationPhases: {
      initial: 0.5,
      accelerating: 2
    }
  },
  
  // ----------------------------------------------------------------
  // VISUAL CONFIGURATION
  // ----------------------------------------------------------------
  visual: {
    // 3D perspective settings
    perspective: {
      mobile: 800,
      desktop: 800,
      desktop_large: 1500
    },
    
    // Perspective origin (percentage)
    perspectiveOrigin: {
      mobile: '50% 40%',
      desktop: '50% 90%'
    },
    
    // Initial opacity for fading rows
    fadeOpacity: {
      start: 0,
      end: 1
    },
    
    // Scale during fade (kept at 1 to prevent alignment issues)
    fadeScale: {
      start: 1,
      end: 1
    },
    
    // Card stagger delay (ms between each card in a row)
    cardStaggerDelay: 30,
    
    // Z-index base (increments per row)
    zIndexBase: 1
  },
  
  // ----------------------------------------------------------------
  // PERFORMANCE CONFIGURATION
  // ----------------------------------------------------------------
  performance: {
    // Memory thresholds
    memoryWarningThreshold: 80, // Percentage of heap limit
    
    // FPS warning threshold
    fpsWarningThreshold: 30,
    
    // Update buffer (pixels beyond viewport to update)
    updateBuffer: 3, // Multiplied by row height
    
    // Visibility buffer (pixels beyond viewport to render)
    visibilityBuffer: 2, // Multiplied by row height
    
    // Card pool size (multiplier of cardData length)
    cardPoolMultiplier: 20,
    
    // Card data pool size
    cardDataPoolSize: 200,
    
    // Memory check interval (ms)
    memoryCheckInterval: 1000
  },
  
  // ----------------------------------------------------------------
  // RESPONSIVE BREAKPOINTS
  // ----------------------------------------------------------------
  breakpoints: {
    mobile_small: 480,
    mobile: 640,
    tablet: 768,
    desktop_small: 1024,
    desktop_medium: 1440,
    desktop_large: 1920
  }
}

// ----------------------------------------------------------------
// HELPER FUNCTIONS
// ----------------------------------------------------------------

/**
 * Get scroll speed based on viewport width
 */
export function getScrollSpeed(width) {
  const { breakpoints, motion } = ANIMATION_CONFIG
  
  if (width < breakpoints.mobile_small) return motion.scrollSpeed.mobile_small
  if (width < breakpoints.tablet) return motion.scrollSpeed.mobile
  if (width < breakpoints.desktop_small) return motion.scrollSpeed.tablet
  if (width < breakpoints.desktop_medium) return motion.scrollSpeed.desktop_small
  return motion.scrollSpeed.desktop
}

/**
 * Get row height based on viewport width
 */
export function getRowHeight(width) {
  const { breakpoints, spacing } = ANIMATION_CONFIG
  
  if (width < breakpoints.mobile_small) return spacing.rowHeight.mobile_small
  if (width < breakpoints.mobile) return spacing.rowHeight.mobile
  if (width < breakpoints.tablet) return spacing.rowHeight.tablet_small
  if (width < breakpoints.desktop_small) return spacing.rowHeight.tablet
  if (width < breakpoints.desktop_medium) return spacing.rowHeight.desktop_small
  return spacing.rowHeight.desktop
}

/**
 * Get cards per row based on viewport width
 */
export function getCardsPerRow(width) {
  const { breakpoints, cardsPerRow, spacing } = ANIMATION_CONFIG
  
  // Calculate based on available width
  const cardWidth = width < breakpoints.tablet ? spacing.cardWidth.mobile : spacing.cardWidth.desktop
  const gap = width < breakpoints.tablet ? spacing.cardGap.mobile : spacing.cardGap.desktop
  const padding = width < breakpoints.tablet ? spacing.padding.mobile : spacing.padding.desktop
  
  const availableWidth = width - padding
  const cardsWithGap = Math.floor(availableWidth / (cardWidth + gap))
  
  // Use configured minimums
  if (width < breakpoints.mobile_small) return Math.max(cardsPerRow.mobile_small, cardsWithGap)
  if (width < breakpoints.mobile) return Math.max(cardsPerRow.mobile, cardsWithGap)
  if (width < breakpoints.tablet) return Math.max(cardsPerRow.tablet_small, cardsWithGap)
  if (width < breakpoints.desktop_small) return Math.max(cardsPerRow.tablet, cardsWithGap)
  if (width < breakpoints.desktop_medium) return Math.max(cardsPerRow.desktop_small, cardsWithGap)
  if (width < breakpoints.desktop_large) return Math.max(cardsPerRow.desktop, cardsWithGap)
  return Math.max(cardsPerRow.desktop_large, cardsWithGap)
}